import { useCallback, useEffect, useState } from 'react'
import { teacherPriorityApi, type CancellationRequestResponse } from '@/services/api/teacherPriority'
import { Check, X } from 'lucide-react'
import { extractErrorMessage } from '@/utils/errorHandler'
import { dispatchCascadeStateChanged } from '@/utils/cascadeEvents'

interface StornoRequestsPanelProps {
  /** When provided, only show stornos for this request. */
  requestId?: string
  /** Compact mode (inside a drawer/detail). */
  compact?: boolean
  onResponded?: () => void
}

/**
 * Lists pending storno confirmations addressed to the current teacher and lets
 * them accept/decline in-app (in addition to the email links).
 */
export function StornoRequestsPanel({ requestId, compact, onResponded }: StornoRequestsPanelProps) {
  const [stornos, setStornos] = useState<CancellationRequestResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const filterMine = useCallback((all: CancellationRequestResponse[]) => {
    return all
      .map(s => ({
        ...s,
        items: (s.items || []).filter(i => i.is_mine && i.status === 'pending'),
      }))
      .filter(s => s.items && s.items.length > 0 && (!requestId || s.substitution_request_id === requestId))
  }, [requestId])

  const fetchStornos = useCallback(async () => {
    setLoading(true)
    try {
      const all = await teacherPriorityApi.getCancellationRequests()
      setStornos(filterMine(all))
    } catch (e) {
      setError(extractErrorMessage(e, { loadError: 'Storno-Anfragen konnten nicht geladen werden.' }))
    } finally {
      setLoading(false)
    }
  }, [filterMine])

  const silentRefresh = useCallback(async () => {
    try {
      const all = await teacherPriorityApi.getCancellationRequests()
      const mine = filterMine(all)
      setStornos(prev => {
        if (prev.length === mine.length && JSON.stringify(prev) === JSON.stringify(mine)) return prev
        return mine
      })
    } catch {
      // silent
    }
  }, [filterMine])

  useEffect(() => {
    fetchStornos()
  }, [fetchStornos])

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      silentRefresh()
    }, 30000)
    return () => clearInterval(id)
  }, [silentRefresh])

  const respond = async (cancellationId: string, accept: boolean) => {
    setBusy(cancellationId)
    setError(null)
    try {
      if (accept) await teacherPriorityApi.acceptCancellationRequest(cancellationId)
      else await teacherPriorityApi.declineCancellationRequest(cancellationId)
      dispatchCascadeStateChanged()
      await fetchStornos()
      onResponded?.()
    } catch (e) {
      setError(extractErrorMessage(e, { loadError: 'Antwort konnte nicht gesendet werden.' }))
    } finally {
      setBusy(null)
    }
  }

  if (loading && stornos.length === 0) return null
  if (stornos.length === 0) return null

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      {stornos.map(s => (
        <div key={s.id} className="border border-orange-200 bg-orange-50 rounded-lg p-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-orange-900">
                Storno-Anfrage ({s.items?.length} Lektion{(s.items?.length || 0) !== 1 ? 'en' : ''})
              </p>
              {s.reason && <p className="text-xs text-orange-800 mt-0.5">Grund: {s.reason}</p>}
              {s.expires_at && (
                <p className="text-xs text-orange-700 mt-0.5">
                  Frist bis {new Date(s.expires_at).toLocaleString('de-CH')}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => respond(s.id, true)}
              disabled={busy !== null}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" /> Alle annehmen
            </button>
            <button
              onClick={() => respond(s.id, false)}
              disabled={busy !== null}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
            >
              <X className="w-3.5 h-3.5" /> Alle ablehnen
            </button>
          </div>
        </div>
      ))}
      {error && <div className="p-2 bg-red-100 text-red-700 rounded text-xs">{error}</div>}
    </div>
  )
}
